import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../supabase/client';
import { loadFromCache, CACHE_KEYS, saveToProfilesCache, updateCacheTimestamp } from '../data/cache';
import { setOtherUserProfile } from '../Redux/profilesSlice';

/**
 * Hook to load and cache profile data for any user
 *
 * Flow (three-tier cache):
 * 1. Check Redux - if found, return immediately (fresh, fetched this session)
 * 2. Check localStorage - if found, return immediately and trigger background reload (stale)
 * 3. If not in either, load from Supabase
 *
 * Cache format: { userId: { data: profile, last_accessed: timestamp } }
 * Cache filtering: Happens when saving new data (via saveToProfilesCache)
 * - Removes entries older than CACHE_TTL_DAYS (7 days)
 *
 * @param {string} userId - The user ID to load profile data for
 * @param {boolean} reload - If true, always reload from Supabase. If false, only load if not in cache
 * @returns {object} - { profile, loading }
 */
export default function useProfileData(userId, reload = false) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const dispatch = useDispatch();

  const profiles = useSelector((state) => state.profiles.profiles);

  useEffect(() => {
    if (!userId) {
      setProfile(null);
      return;
    }

    // Check Redux first (freshest, session-tracked)
    const reduxProfile = profiles[userId];
    if (reduxProfile && !reload) {
      setProfile(reduxProfile);
      return;
    }

    // Check localStorage cache
    const cache = loadFromCache(CACHE_KEYS.OTHER_PROFILES) || {};
    const cachedEntry = cache[userId];
    const cachedProfileData = cachedEntry?.data;

    if (cachedProfileData && !reload) {
      // Update last_accessed timestamp
      updateCacheTimestamp(CACHE_KEYS.OTHER_PROFILES, userId);
      // Set immediately for instant display
      setProfile(cachedProfileData);
      // Trigger background reload
      loadProfileFromSupabase(userId, true);
      return;
    }

    // Not in cache or reload=true - load from Supabase
    loadProfileFromSupabase(userId, false);
  }, [userId, reload]);

  const loadProfileFromSupabase = async (id, isBackground) => {
    if (!isBackground) {
      setLoading(true);
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (!isBackground) {
        setLoading(false);
      }
      return;
    }

    if (data) {
      // Update Redux state
      dispatch(setOtherUserProfile({ userId: id, profile: data }));
      // Update local state
      setProfile(data);
      // Update cache with fresh data and timestamp
      saveToProfilesCache(id, data);
    }

    if (!isBackground) {
      setLoading(false);
    }
  };

  return { profile, loading };
}
